/**
 * TerminalStockRequestTriage — decide a stock request on the handheld.
 *
 * Opened from the QuickScan sheet ONLY when the scanned request is still
 * awaiting triage AND this terminal is paired to its SUPPLYING branch. The
 * backend enforces the same rule (tenant-scoped lookup + terminal-branch
 * check on /triage and /stock-on-hand), so this panel is a convenience, not
 * the gate. Everyone else — a phone scan, or a terminal at another branch —
 * gets the read-only view + print.
 *
 * Flow mirrors the web triage: on-hand hint per line → "Fill from my stock"
 * (auto-assigns Transfer to fully-covered lines) → hand-flip the leftovers
 * (Transfer / Supplier / Can't) → manager PIN → submit. Supplier lines are
 * notes-only since BT Batch 2, so there's no picker or price on the device.
 * Nothing fires without the PIN.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Truck, Package, Ban, ClipboardCheck, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CHOICES = [
  { key: 'transfer',     label: 'Transfer',  Icon: Truck,   on: 'bg-emerald-600 text-white border-emerald-600', off: 'bg-white text-slate-600 border-slate-200' },
  { key: 'supplier_po',  label: 'Supplier',  Icon: Package, on: 'bg-violet-600 text-white border-violet-600',   off: 'bg-white text-slate-600 border-slate-200' },
  { key: 'unfulfilled',  label: "Can't",     Icon: Ban,     on: 'bg-rose-600 text-white border-rose-600',       off: 'bg-white text-slate-600 border-slate-200' },
];

export default function TerminalStockRequestTriage({
  api, session, requestId, requestNumber, onClose, onTriaged,
  inline = false,     // true → embed in a page (DocViewer Terminal Actions), no fixed wrapper/header
  initialPin = '',    // DocViewer passes the Tier-2 unlock PIN so it isn't typed twice
}) {
  const [doc, setDoc] = useState(null);
  const [onHand, setOnHand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [assign, setAssign] = useState({});        // item.id → choice key
  const [autoResult, setAutoResult] = useState(null);
  const [pin, setPin] = useState(initialPin || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(null);           // triage response
  const pinRef = useRef(null);

  // Load the request through the AUTHED, tenant-scoped endpoint (a code from
  // another tenant 404s here even though the public view showed it) + on-hand.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const [r, oh] = await Promise.all([
          api.get(`/stock-requests/${requestId}`),
          api.get(`/stock-requests/${requestId}/stock-on-hand`),
        ]);
        if (!alive) return;
        setDoc(r.data);
        setOnHand(oh.data?.on_hand || {});
      } catch (e) {
        if (!alive) return;
        const st = e.response?.status;
        setLoadError(st === 403 ? (e.response?.data?.detail || 'This terminal cannot edit this request.')
                   : st === 404 ? 'This request belongs to another organization or no longer exists.'
                   : (e.response?.data?.detail || 'Could not load the request.'));
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, requestId]);

  const items = doc?.items || [];
  const covered = useCallback((it) => {
    if (!onHand) return false;
    const have = parseFloat(onHand[it.product_id] ?? 0) || 0;
    const need = parseFloat(it.qty) || 0;
    return need > 0 && have >= need;
  }, [onHand]);

  const fillFromStock = () => {
    const cov = items.filter(covered);
    const ids = new Set(cov.map(i => i.id));
    setAssign(prev => { const n = { ...prev }; items.forEach(i => { if (ids.has(i.id)) n[i.id] = 'transfer'; }); return n; });
    setAutoResult({ assigned: cov.length, needsCall: items.length - cov.length });
    if (!cov.length) toast.info('No line is fully covered by your stock — decide each one below.');
  };

  const assignedCount = useMemo(() => items.filter(i => assign[i.id]).length, [items, assign]);
  const unassignedCount = items.length - assignedCount;

  const submit = async () => {
    if (!pin.trim()) { setSubmitError('PIN required'); pinRef.current?.focus(); return; }
    if (assignedCount === 0) { setSubmitError('Assign at least one line'); return; }
    setSubmitting(true); setSubmitError('');
    try {
      const assignments = items.filter(i => assign[i.id]).map(i => ({ item_id: i.id, fulfillment_type: assign[i.id] }));
      const res = await api.post(`/stock-requests/${requestId}/triage`, { pin, assignments });
      setDone(res.data);
      toast.success(`${requestNumber || 'Request'} triaged${res.data?.bto_id ? ' — transfer draft created' : ''}`);
      onTriaged?.(res.data);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || 'Triage failed');
    }
    setSubmitting(false);
  };

  const wrapperCls = inline
    ? 'flex flex-col rounded-xl border border-teal-200 overflow-hidden bg-white'
    : 'fixed inset-0 z-[60] flex flex-col bg-white';
  return (
    <div className={wrapperCls} data-testid="terminal-triage-overlay">
      {/* Header — matches the other terminal overlays; compact when embedded */}
      <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${inline ? 'bg-teal-50' : 'bg-white'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardCheck size={16} className="text-teal-700 shrink-0" />
          <h2 className="font-bold text-sm text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>
            Triage {requestNumber || 'request'}
          </h2>
        </div>
        {!inline && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="terminal-triage-close">
            <X size={18} className="text-slate-500" />
          </button>
        )}
      </div>

      <div className={`${inline ? '' : 'flex-1 overflow-y-auto'} px-4 py-3 space-y-3`}>
        {loading && (
          <div className="py-12 flex items-center justify-center gap-2 text-sm text-slate-400">
            <Loader2 size={16} className="animate-spin" /> Loading request…
          </div>
        )}

        {!loading && loadError && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2" data-testid="terminal-triage-blocked">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">View only</p>
              <p className="text-xs mt-0.5">{loadError}</p>
            </div>
          </div>
        )}

        {!loading && !loadError && doc && done && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="terminal-triage-done">
            <p className="font-semibold flex items-center gap-2"><CheckCircle2 size={16} /> Fulfillment plan generated</p>
            <p className="text-xs mt-1">
              {done.bto_id ? 'A transfer draft was created — set prices and send it from the web app. ' : ''}
              {done.unfulfilled ? `${done.unfulfilled} line(s) marked cannot fulfill.` : ''}
            </p>
            {!inline && (
              <button onClick={onClose} className="mt-3 w-full py-2.5 rounded-xl bg-[#1A4D2E] text-white text-sm font-semibold" data-testid="terminal-triage-done-close">Done</button>
            )}
          </div>
        )}

        {!loading && !loadError && doc && !done && (
          <>
            {/* Route */}
            <div className="text-xs text-slate-500">
              <span className="font-semibold text-slate-800">{doc.requesting_branch_name || 'Requesting branch'}</span>
              <span className="mx-1.5 uppercase tracking-wider text-[10px]">requests from</span>
              <span className="font-semibold text-slate-800">{session?.branchName || 'your branch'}</span>
              {doc.notes && <p className="italic mt-1">📝 {doc.notes}</p>}
            </div>

            {/* Fill from my stock */}
            <div className="rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5 flex items-center justify-between gap-3" data-testid="terminal-fill-from-stock">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-teal-900">Fill from my stock</p>
                <p className="text-[11px] text-teal-700">Sets Transfer on every line you fully cover.</p>
                {autoResult && (
                  <p className="text-[11px] text-teal-800 mt-0.5" data-testid="terminal-fill-result">
                    {autoResult.assigned} of {items.length} assigned
                    {autoResult.needsCall > 0 && <> · <span className="font-medium text-amber-700">{autoResult.needsCall} need your call</span></>}
                  </p>
                )}
              </div>
              <button onClick={fillFromStock} disabled={!onHand}
                className="shrink-0 px-3 py-2 rounded-xl bg-teal-600 text-white text-xs font-semibold active:scale-95 disabled:opacity-50"
                data-testid="terminal-fill-from-stock-btn">
                <Truck size={13} className="inline mr-1" /> Fill
              </button>
            </div>

            {/* Lines */}
            <div className="space-y-2">
              {items.map(it => {
                const have = parseFloat(onHand?.[it.product_id] ?? 0) || 0;
                const ok = covered(it);
                const sel = assign[it.id] || '';
                return (
                  <div key={it.id} className="rounded-xl border border-slate-200 p-3" data-testid={`terminal-triage-line-${it.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-slate-800 leading-tight">{it.product_name}</p>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-slate-800">{it.qty} {it.unit}</p>
                        {onHand && (
                          <p className={`text-[10px] ${ok ? 'text-teal-600' : 'text-amber-600'}`} data-testid={`terminal-onhand-${it.id}`}>
                            {ok ? `✓ ${have} on hand` : `only ${have} on hand`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 mt-2">
                      {CHOICES.map(c => (
                        <button key={c.key} type="button"
                          onClick={() => setAssign(prev => ({ ...prev, [it.id]: prev[it.id] === c.key ? '' : c.key }))}
                          className={`py-2 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 active:scale-95 ${sel === c.key ? c.on : c.off}`}
                          data-testid={`terminal-choice-${it.id}-${c.key}`}>
                          <c.Icon size={12} /> {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer — PIN + submit (only while editable) */}
      {!loading && !loadError && doc && !done && (
        <div className="border-t bg-amber-50 px-4 py-3 shrink-0 space-y-2">
          <p className="text-[11px] text-amber-800">
            {assignedCount} of {items.length} line(s) assigned
            {unassignedCount > 0 && <> · <span className="font-medium">{unassignedCount} unassigned will be marked cannot fulfill</span></>}
          </p>
          <div className="flex gap-2">
            <input ref={pinRef} type="password" inputMode="numeric" autoComplete="one-time-code" data-lpignore="true"
              value={pin} onChange={e => { setPin(e.target.value); setSubmitError(''); }}
              onKeyDown={e => e.key === 'Enter' && submit()}
              placeholder="Manager PIN" className="flex-1 h-11 rounded-xl border border-amber-300 px-3 text-center font-mono tracking-widest text-sm"
              data-testid="terminal-triage-pin" />
            <button onClick={submit} disabled={submitting}
              className="h-11 px-4 rounded-xl bg-[#1A4D2E] text-white text-sm font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-60"
              data-testid="terminal-triage-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
              Generate plan
            </button>
          </div>
          {submitError && <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="terminal-triage-error"><AlertTriangle size={12} /> {submitError}</p>}
        </div>
      )}
    </div>
  );
}
